/**
 * The library's token text → printer bytes.
 *
 * `bill.getPrintContent` does not return bytes: it returns lines, where a line starting with a
 * mnemonic (`ESC a 1`, `GS ! 1`, `LF`) is a command and anything else is literal text. This
 * turns those lines into the bytes a thermal printer takes.
 *
 * ONE definition for every renderer-side host. It replaces the byte-identical copy in
 * `apps/mobile/src/hooks/usePrinter/utils.js` and is pinned against
 * `apps/desktop/src/printer.js` `fnParseContent` — which stays, because it is what a LAN master
 * and the NW shell use to tokenize text they receive (`escpos.equivalence.test.ts`).
 *
 * Ported to match desktop BYTE FOR BYTE, quirks included:
 * - numeric arguments are base 10 (desktop's `parseInt(x, 10)`; mobile's radix-less call read
 *   `0x1b` as hex — the library emits decimal, so no real receipt differs);
 * - blank lines are dropped, and text lines get NO newline — breaks come only from `LF`;
 * - PLAIN keeps text and `LF`, drops every other command, and ends with two newlines;
 * - the mnemonic lookup is a plain object, so a line whose first word is an `Object.prototype`
 *   name (`constructor …`) is misread as a command exactly as on desktop. Flagged, not fixed:
 *   fixing it here alone would make the LAN master and this encoder disagree.
 */
import type { ContentType } from "../types";

const ESC_POS: Record<string, number> = {
  ESC: 0x1b,
  HT: 0x09,
  LF: 0x0a,
  FF: 0x0c,
  CR: 0x0d,
  CAN: 0x18,
  FS: 0x1c,
  GS: 0x1d,
};

export function encodeEscPos(contentType: ContentType | string | undefined, content: string): Uint8Array {
  const data: number[] = [];
  const pushText = (text: string) => {
    for (let i = 0; i < text.length; i++) data.push(text.charCodeAt(i));
  };

  const lines = content.split("\n");
  if (contentType === "FORMATTED") {
    for (const line of lines) {
      if (line.trim().length === 0) continue;
      const parts = line.split(" ");
      const command = ESC_POS[parts[0]];
      if (!command) {
        pushText(line);
        continue;
      }
      data.push(command);
      for (let i = 1; i < parts.length; i++) {
        const n = parseInt(parts[i], 10);
        if (!isNaN(n)) data.push(n);
        else pushText(parts[i]);
      }
    }
  } else {
    for (const line of lines) {
      if (line.trim().length === 0) continue;
      const parts = line.split(" ");
      if (!ESC_POS[parts[0]]) pushText(line);
      else if (parts[0] === "LF") pushText("\n");
    }
    pushText("\n\n");
  }
  return new Uint8Array(data);
}
